import OrderItem from "@/modules/delivery/core/models/OrderItem";
import OrderPromo from "@/modules/delivery/core/models/OrderPromo";
import {DELIVERY,  TAKE_AWAY} from "@/modules/delivery/constants/OrderDelivery";
import {BAR, SALON} from "@/modules/delivery/constants/OrderOrigin";
import BaseOrderService from "@/modules/delivery/core/services/BaseOrderService";

class CartService extends BaseOrderService{
    constructor(order, settings, categoryService) {
        super(order, settings, categoryService)
        this.className = "CartService";
    }

    clearItems() {
        this.order.items = []
    }

    createOrderPromo(promo){
        let orderPromo = new OrderPromo({promo, price: promo.price, items: []})
        return orderPromo
    }

    addOrderPromo(orderPromo) {
        if(orderPromo instanceof OrderPromo) {
            this.order.promos.push(orderPromo)
        }else{
            throw new Error("OrderPromo must be an instance of OrderPromo")
        }

    }

    getQuantityByPromo(promo) {
        return this.order.promos.filter(p => p.promo.id === promo.id).length
    }

    removeOrderPromo(promo) {
        let index = this.order.promos.findIndex(p => p.promo._id === promo.id)
        if (index >= 0) {
            this.order.promos.splice(index, 1)
        }
    }

    addOrderItem(product, category, order = 999) {
        let item = this.order.items.find(i => i.hasProductAndCategory(product, category))
        if (item) {
            item.quantity++
            item.amount += product.price + (category.surcharge ? category.surcharge : 0)
        } else {
            this.order.items.push(new OrderItem({
                        product: product,
                        category: category,
                        quantity: 1,
                        order: order,
                        amount: product.price + (category.surcharge ? category.surcharge : 0)
                    }
                )
            )
        }
    }

    changeOrderItemQuantity(product, category, quantity, order){
        if (quantity === 0 || !quantity) {
            let index = this.order.items.findIndex(i => i.product.id === product.id && i.category.id === category.id)
            this.order.items.splice(index, 1)
            return
        }

        let item = this.order.items.find(i => i.product.id === product.id && i.category.id === category.id)
        if (item) {
            item.quantity = quantity
            item.amount = item.quantity ? product.price * item.quantity : 0
        } else {
            this.order.items.push(new OrderItem({
                product: product,
                category: category,
                quantity: quantity,
                amount: quantity * product.price,
                order: order
            }))
        }
    }

    removeOrderItemsByDeliveryMode(deliveryMode) {
        let mode = deliveryMode === 'DELIVERY' ? 'Delivery' : 'Take Away'
        this.order.items = this.order.items.filter(item => item.product?.salesChannelsConfig?.mostrador.modes.includes(mode))
    }

    removeOrderItem(product, category) {
        let item = this.order.items.find(i => i.hasProductAndCategory(product, category))
        if (item && item.quantity > 0) {
            item.quantity--
            item.amount -= product.price
        }

        if (item.quantity === 0) {
            let index = this.order.items.findIndex(i => i.hasProductAndCategory(product, category))
            this.order.items.splice(index, 1)
        }
    }

    getItemsByCategory(category) {
        return this.order.items.filter(i => i.hasCategory(category))
    }

    hasCategoryItems(category) {
        return this.order.items.some(i => i.hasCategory(category))
    }

    getQuantityByCategory(category) {
        let quantity = this.order.items
            .filter(i => i.hasCategory(category))
            .reduce((a, v) => a + v.quantity, 0)

        return quantity
    }

    getAmountByCategory(category) {
        let amount = this.order.items
            .filter(i => i.hasCategory(category))
            .reduce((a, v) => a + v.amount, 0)

        return amount
    }

    getQuantityByProductAndCategory(product, category) {
        let quantity =  this.order.items
            .filter(i => i.hasProductAndCategory(product, category))
            .reduce((a, v) => a + v.quantity, 0)

        return quantity > 0 ? quantity : null
    }

    getAmountByProductAndCategory(product, category) {
        return this.order.items
            .filter(i => i.hasProductAndCategory(product, category))
            .reduce((a, v) => a + v.amount, 0)
    }

    get getItemsQuantity() {
        return this.order.items.reduce((a, v) => a + v.quantity, 0)
    }

    get getPromoQuantity() {
        return this.order.promos.length
    }

    get getPromoItemsQuantity() {
        return this.order.promos.reduce((q, orderPromo) => q + orderPromo.items.reduce((a, v) => a + v.quantity, 0),0)
    }

    get getTotalQuantity() {
        return this.getItemsQuantity + this.getPromoItemsQuantity
    }

    get getItemsAmount() {
        return this.order.items.reduce((a, v) => a + v.amount, 0)
    }

    get getPromosAmount() {
        return this.order.promos.reduce((a, v) => a + v.price, 0)
    }

    get getPromosAndItemsAmount() {
        return this.getItemsAmount + this.getPromosAmount
    }

    get getCategoryWithItems() {
        this.assertCategoryService()
        return this.categoryService.categories.filter(c => this.order.items.some(i => i.hasCategory(c)))
    }

    get isMinimumQuantityPerCategoryReached() {
        let value = true

        for(let category of this.getCategoryWithItems){

            if (this.isMinimumQuantityCategoryReached(category) === false) {
                value = false
            }
        }

        return value
    }

    getCategoryMinimunQuantity(category) {

            if (this.order.delivery.mode === TAKE_AWAY) {

                if(category && category.minimumTakeAwayQuantity){
                    return category.minimumTakeAwayQuantity
                }
                return this.categoryService.getCategoryMinimunQuantityForTakeAway(category)

            } else if (this.order.delivery.mode === DELIVERY) {

                if(category && category.minimumDeliveryQuantity){
                    return category.minimumDeliveryQuantity
                }

                return this.categoryService.getCategoryMinimunQuantityForDelivery(category)
            }
            return 0
    }

    get isMinimumQuantityCategoryReached() {
        return category => {
            let categoryQuantity = this.getQuantityByCategory(category)
            if (categoryQuantity === 0) return true

            let minimunCategoryQuantity = this.getCategoryMinimunQuantity(category)
            if (categoryQuantity > 0 && categoryQuantity < minimunCategoryQuantity) {
                return false
            }

            return true
        }
    }

    get getCategoriesBelowMinimunQuantity() {
        return this.getCategoryWithItems.filter(category => this.isMinimumQuantityCategoryReached(category) === false)
    }

    get isMinimumTotalQuantityReached(){
        if(this.order.origin === BAR) {
            return this.getTotalQuantity >= 1
        }else{
            return this.getTotalQuantity >= this.settings.minimumQuantity
        }
    }

    assertCategoryService(){
        if(!this.categoryService){
            throw new Error("categoryService is not set")
        }
    }

    assertOrder(){
        if(!this.order){
            throw new Error("CartService is not initialized. Order is not set")
        }
    }
    assertInitialized() {
        this.assertOrder()
        this.assertCategoryService()
        return true
    }
    get isReady(){


        this.assertInitialized()


        if(this.order.origin === BAR || this.order.origin === SALON){
            return this.getTotalQuantity > 0
        }

        if (this.getItemsQuantity == 0 && this.order.promos.length === 0) {
            return false
        }

        if(this.isMinimumQuantityPerCategoryReached === false){
            return false
        }

        if (!this.isMinimumTotalQuantityReached) {
            return false
        }

        return true
    }


    //DISCOUNTING

    get getDeliveryCost() {
        if(this.order.deliveryFree) return 0
        return this.order.delivery.cost
    }

    calculateDiscountByRate(amount) {
        if (this.order.discountRate > 0) {
            return Math.round(amount * this.order.discountRate / 100)
        }
        return 0
    }

    get getTotalDiscount() {
        let discount = 0
        if (this.order.discountAmount > 0) {
            discount += discount + this.order.discountAmount
        }

        if (this.order.discountRate > 0) {
            discount += discount + this.calculateDiscountByRate(this.getPromosAndItemsAmount)
        }
        return discount
    }

    get getTotalAmountWithDiscount(){
        return this.getPromosAndItemsAmount - this.getTotalDiscount + this.getDeliveryCost
    }

    get getTotalAmount(){
        return this.getPromosAndItemsAmount +  this.getDeliveryCost
    }

    get hasDiscount(){
        return this.order.discountAmount > 0 || this.order.discountRate > 0
    }
}

export default CartService;
